import React, { useState, useEffect, useCallback } from 'react';
import {
  Store, MapPin, Phone, Mail, Clock, Truck, Lock, Save,
  CheckCircle, Timer, Shield, Settings, Image as ImageIcon,
  Megaphone, Zap, Facebook, Instagram, AlertCircle, X
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import ShopLayout from '@/components/shop/ShopLayout';
import AutoPostHistory from '@/components/shop/AutoPostHistory';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS = { monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday', thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday' };

const AUTO_LOCK_OPTIONS = [
  { label: 'Manual Only', value: 0 },
  { label: '1 min', value: 1 },
  { label: '3 min', value: 3 },
  { label: '5 min', value: 5 },
  { label: '10 min', value: 10 },
  { label: '15 min', value: 15 },
];

const Section = ({ icon: Icon, title, desc, children }) => (
  <div className="bg-white border-2 border-slate-100 p-6" data-testid={`section-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center gap-2 mb-1">
      <Icon className="w-5 h-5 text-[#22c55e]" />
      <h2 className="text-lg font-bold text-[#1e3a5f]">{title}</h2>
    </div>
    {desc && <p className="text-sm text-slate-500 mb-5">{desc}</p>}
    {children}
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <label className="block text-sm font-medium text-[#1e3a5f] mb-1">{label}</label>
    {children}
  </div>
);

const Input = ({ value, onChange, ...props }) => (
  <input value={value} onChange={e => onChange(e.target.value)}
    className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-sm text-sm focus:border-[#1e3a5f] focus:outline-none"
    {...props} />
);

export default function ShopSettingsPage() {
  const { user, api: authApi, token } = useAuth();
  const { t } = useLanguage();
  const shopId = user?.shop_id;

  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form state
  const [form, setForm] = useState({
    name: '', description: '', image_url: '',
    address: '', city: '', postal_code: '', phone: '', email: '',
    delivery_enabled: false, takeaway_enabled: false,
    delivery_radius: '', delivery_min_order: '', delivery_fee: '',
    report_emails: '', currency: 'EUR',
    opening_hours: {},
  });

  // POS Lock state
  const [posLockEnabled, setPosLockEnabled] = useState(false);
  const [autoLockMinutes, setAutoLockMinutes] = useState(0);
  const [adminPin, setAdminPin] = useState('');
  const [hasPin, setHasPin] = useState(false);

  // Social automation state
  const [socialSettings, setSocialSettings] = useState({
    auto_post_enabled: false,
    auto_post_trigger: 'manual_only',
    auto_post_platforms: ['facebook'],
    post_style: 'exciting',
    language: 'es',
    last_auto_post: null,
  });
  const [socialConnections, setSocialConnections] = useState({ facebook: null });
  const [savingSocial, setSavingSocial] = useState(false);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchSettings = useCallback(async () => {
    if (!shopId) return;
    try {
      const res = await fetch(`${API}/api/shops/${shopId}`, { headers });
      if (res.ok) {
        const s = await res.json();
        setShop(s);
        setForm({
          name: s.name || '', description: s.description || '', image_url: s.image_url || '',
          address: s.address || '', city: s.city || '', postal_code: s.postal_code || '',
          phone: s.phone || '', email: s.email || '',
          delivery_enabled: s.delivery_enabled || false,
          takeaway_enabled: s.takeaway_enabled || false,
          delivery_radius: s.delivery_radius || '',
          delivery_min_order: s.delivery_min_order || '',
          delivery_fee: s.delivery_fee || '',
          report_emails: s.report_emails || '',
          currency: s.currency || 'EUR',
          opening_hours: s.opening_hours || {},
        });
        setPosLockEnabled(s.pos_lock_enabled || false);
        setAutoLockMinutes(s.pos_auto_lock_minutes || 0);
      }

      const pinRes = await fetch(`${API}/api/restaurants/${shopId}/admin-pin`, { headers });
      if (pinRes.ok) {
        const p = await pinRes.json();
        setHasPin(p.has_pin);
        setAdminPin(p.pin || '');
      }

      // Social automation
      try {
        const [ssRes, scRes] = await Promise.all([
          authApi.get(`/shops/${shopId}/social/settings`),
          authApi.get(`/shops/${shopId}/social/connections`),
        ]);
        setSocialSettings({
          auto_post_enabled: ssRes.data.auto_post_enabled ?? false,
          auto_post_trigger: ssRes.data.auto_post_trigger ?? 'manual_only',
          auto_post_platforms: ssRes.data.auto_post_platforms ?? ['facebook'],
          post_style: ssRes.data.post_style ?? 'exciting',
          language: ssRes.data.language ?? 'es',
          last_auto_post: ssRes.data.last_auto_post ?? null,
        });
        setSocialConnections(scRes.data);
      } catch (e) { /* social endpoints optional */ }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [shopId, token]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const setField = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const setHours = (day, field, val) => {
    setForm(f => ({
      ...f,
      opening_hours: {
        ...f.opening_hours,
        [day]: { ...(f.opening_hours[day] || {}), [field]: val },
      },
    }));
  };

  const toggleDayClosed = (day) => {
    setForm(f => ({
      ...f,
      opening_hours: {
        ...f.opening_hours,
        [day]: { ...(f.opening_hours[day] || {}), closed: !(f.opening_hours[day]?.closed) },
      },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.delivery_radius) payload.delivery_radius = parseFloat(payload.delivery_radius);
      if (payload.delivery_min_order) payload.delivery_min_order = parseFloat(payload.delivery_min_order);
      if (payload.delivery_fee) payload.delivery_fee = parseFloat(payload.delivery_fee);

      await authApi.put(`/shops/${shopId}`, payload);
      toast.success('Settings saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handlePosLockToggle = async (enabled) => {
    setPosLockEnabled(enabled);
    try {
      await fetch(`${API}/api/restaurants/${shopId}/pos-lock-settings?pos_lock_enabled=${enabled}&pos_auto_lock_minutes=${autoLockMinutes}`, { method: 'PUT', headers });
    } catch { setPosLockEnabled(!enabled); }
  };

  const handleAutoLockChange = async (minutes) => {
    setAutoLockMinutes(minutes);
    try {
      await fetch(`${API}/api/restaurants/${shopId}/pos-lock-settings?pos_lock_enabled=${posLockEnabled}&pos_auto_lock_minutes=${minutes}`, { method: 'PUT', headers });
    } catch {}
  };

  const handleSavePin = async () => {
    if (!/^\d{4}$/.test(adminPin)) { toast.error('PIN must be 4 digits'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/restaurants/${shopId}/admin-pin`, {
        method: 'PUT', headers, body: JSON.stringify({ pin: adminPin }),
      });
      if (!res.ok) throw new Error((await res.json()).detail);
      setHasPin(true);
      toast.success('PIN saved');
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const handleSaveSocialAutomation = async () => {
    setSavingSocial(true);
    try {
      await authApi.put(`/shops/${shopId}/social/settings`, {
        auto_post_enabled: socialSettings.auto_post_enabled,
        auto_post_trigger: socialSettings.auto_post_trigger,
        auto_post_platforms: socialSettings.auto_post_platforms,
        post_style: socialSettings.post_style,
        language: socialSettings.language,
      });
      toast.success('Automation settings saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSavingSocial(false); }
  };

  const toggleSocialPlatform = (platform) => {
    setSocialSettings(s => {
      const set = new Set(s.auto_post_platforms || []);
      if (set.has(platform)) set.delete(platform); else set.add(platform);
      return { ...s, auto_post_platforms: [...set] };
    });
  };

  const dismissLastAutoPost = async () => {
    try {
      await authApi.post(`/shops/${shopId}/social/settings/dismiss-last-auto-post`);
      setSocialSettings(s => ({ ...s, last_auto_post: null }));
    } catch (e) { toast.error('Could not dismiss'); }
  };

  const formatTimeAgo = (isoString) => {
    if (!isoString) return '';
    const diff = (Date.now() - new Date(isoString).getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} h ago`;
    return `${Math.floor(diff / 86400)} d ago`;
  };

  if (loading) return (
    <ShopLayout><div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-4 border-[#1e3a5f]/20 border-t-[#1e3a5f] rounded-full animate-spin" />
    </div></ShopLayout>
  );

  return (
    <ShopLayout>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="shop-settings-page">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-[#1e3a5f]">Settings</h1>
          <button onClick={handleSave} disabled={saving}
            className="vf-btn-primary flex items-center gap-2 disabled:opacity-50"
            data-testid="save-settings-btn">
            <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>

        {/* Basic Information */}
        <Section icon={Store} title="Basic Information" desc="Your shop's core details.">
          <div className="space-y-4">
            <Field label="Shop Name">
              <Input value={form.name} onChange={v => setField('name', v)} data-testid="shop-name" />
            </Field>
            <Field label="Description">
              <textarea value={form.description} onChange={e => setField('description', e.target.value)}
                rows={3} className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-sm text-sm focus:border-[#1e3a5f] focus:outline-none resize-none"
                data-testid="shop-description" />
            </Field>
            <Field label="Shop Image URL">
              <Input value={form.image_url} onChange={v => setField('image_url', v)} placeholder="/api/uploads/products/shop.png or https://..." data-testid="shop-image-url" />
              {form.image_url && (
                <img src={form.image_url.startsWith('/api') ? `${API}${form.image_url}` : form.image_url}
                  alt="" className="w-16 h-16 object-contain mt-2 border border-slate-200 rounded-sm bg-slate-50" />
              )}
            </Field>
          </div>
        </Section>

        {/* Contact & Location */}
        <Section icon={MapPin} title="Contact & Location" desc="Your shop's address and contact details.">
          <div className="space-y-4">
            <Field label="Address">
              <Input value={form.address} onChange={v => setField('address', v)} data-testid="shop-address" />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="City">
                <Input value={form.city} onChange={v => setField('city', v)} data-testid="shop-city" />
              </Field>
              <Field label="Postal Code">
                <Input value={form.postal_code} onChange={v => setField('postal_code', v)} data-testid="shop-postal" />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Phone">
                <Input value={form.phone} onChange={v => setField('phone', v)} type="tel" data-testid="shop-phone" />
              </Field>
              <Field label="Email">
                <Input value={form.email} onChange={v => setField('email', v)} type="email" data-testid="shop-email" />
              </Field>
            </div>
          </div>
        </Section>

        {/* Service Availability */}
        <Section icon={Truck} title="Service Availability" desc="Choose which services your shop offers.">
          <div className="space-y-4">
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="font-semibold text-sm text-slate-700">Delivery</p>
                <p className="text-xs text-slate-500">Deliver orders to customers</p>
              </div>
              <Switch checked={form.delivery_enabled} onCheckedChange={v => setField('delivery_enabled', v)} data-testid="delivery-toggle" />
            </div>
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="font-semibold text-sm text-slate-700">Takeaway</p>
                <p className="text-xs text-slate-500">Customers collect from shop</p>
              </div>
              <Switch checked={form.takeaway_enabled} onCheckedChange={v => setField('takeaway_enabled', v)} data-testid="takeaway-toggle" />
            </div>
          </div>
        </Section>

        {/* Delivery Settings */}
        {form.delivery_enabled && (
          <Section icon={Truck} title="Delivery Settings" desc="Configure delivery radius, fees, and minimum order.">
            <div className="grid grid-cols-3 gap-4">
              <Field label="Delivery Radius (km)">
                <Input type="number" step="0.5" min="0" value={form.delivery_radius} onChange={v => setField('delivery_radius', v)} data-testid="delivery-radius" />
              </Field>
              <Field label="Minimum Order (€)">
                <Input type="number" step="0.5" min="0" value={form.delivery_min_order} onChange={v => setField('delivery_min_order', v)} data-testid="delivery-min-order" />
              </Field>
              <Field label="Delivery Fee (€)">
                <Input type="number" step="0.01" min="0" value={form.delivery_fee} onChange={v => setField('delivery_fee', v)} data-testid="delivery-fee" />
              </Field>
            </div>
          </Section>
        )}

        {/* Currency */}
        <Section icon={Settings} title="Currency" desc="Your shop's pricing currency.">
          <div className="flex gap-3">
            {[{ code: 'EUR', symbol: '€', label: 'Euro', sub: 'European Union' }, { code: 'GBP', symbol: '£', label: 'British Pound', sub: 'United Kingdom' }].map(c => (
              <button key={c.code} onClick={() => setField('currency', c.code)}
                className={`flex items-center gap-3 px-4 py-3 border-2 rounded-sm transition-colors flex-1 ${
                  form.currency === c.code ? 'border-[#22c55e] bg-green-50' : 'border-slate-200 hover:border-slate-300'
                }`} data-testid={`currency-${c.code}`}>
                <span className="text-2xl font-bold text-[#1e3a5f]">{c.symbol}</span>
                <div className="text-left">
                  <p className="text-sm font-medium text-[#1e3a5f]">{c.label}</p>
                  <p className="text-xs text-slate-500">{c.sub}</p>
                </div>
                {form.currency === c.code && <CheckCircle className="w-5 h-5 text-[#22c55e] ml-auto" />}
              </button>
            ))}
          </div>
        </Section>

        {/* Report Emails */}
        <Section icon={Mail} title="Report Emails" desc="End of Day and Financial reports will be sent to these addresses.">
          <Field label="Accountant / Report Email(s)">
            <Input value={form.report_emails} onChange={v => setField('report_emails', v)}
              placeholder="accountant@company.com, admin@company.com" data-testid="report-emails" />
          </Field>
          <p className="text-xs text-slate-400 mt-1">Comma-separated. Reports are sent after each end-of-day close.</p>
        </Section>

        {/* POS Staff Lock */}
        <Section icon={Lock} title="POS Staff Lock" desc="Require staff PIN to use POS. Useful when multiple cashiers share one terminal.">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="font-semibold text-sm text-slate-700">Enable POS Lock</p>
              <p className="text-xs text-slate-500">Staff must enter their 4-digit PIN to use the POS</p>
            </div>
            <Switch checked={posLockEnabled} onCheckedChange={handlePosLockToggle} data-testid="pos-lock-toggle" />
          </div>
          {posLockEnabled && (
            <div className="border-l-4 border-[#22c55e]/30 pl-4 mt-4">
              <div className="flex items-center gap-2 mb-3">
                <Timer className="w-4 h-4 text-slate-500" />
                <p className="text-sm font-medium text-slate-700">Auto-lock after inactivity</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {AUTO_LOCK_OPTIONS.map(opt => (
                  <button key={opt.value} onClick={() => handleAutoLockChange(opt.value)}
                    className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                      autoLockMinutes === opt.value ? 'bg-[#22c55e] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`} data-testid={`auto-lock-${opt.value}`}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </Section>

        {/* Admin POS PIN */}
        <Section icon={Shield} title="Admin POS PIN" desc="Set your 4-digit PIN for quick POS access.">
          <div className="flex items-center gap-3">
            <input type="text" inputMode="numeric" maxLength={4} value={adminPin}
              onChange={e => setAdminPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="0000"
              className="w-32 border-2 border-slate-200 rounded-sm px-4 py-2.5 text-center text-xl font-mono tracking-[0.5em] focus:outline-none focus:border-[#1e3a5f]"
              data-testid="admin-pin-input" />
            <button onClick={handleSavePin} disabled={saving || adminPin.length !== 4}
              className="flex items-center gap-2 px-4 py-2.5 bg-[#1e3a5f] text-white rounded-sm text-sm font-medium disabled:opacity-50"
              data-testid="save-pin-btn">
              <Save className="w-4 h-4" /> {hasPin ? 'Update PIN' : 'Save PIN'}
            </button>
            {hasPin && <span className="flex items-center gap-1 text-[#22c55e] text-sm"><CheckCircle className="w-4 h-4" /> PIN set</span>}
          </div>
        </Section>

        {/* Opening Hours */}
        <Section icon={Clock} title="Opening Hours" desc="Set your shop's opening times. Add breaks with multiple slots per day.">
          <div className="space-y-3">
            {DAYS.map(day => {
              const hours = form.opening_hours[day] || {};
              const isClosed = hours.closed;
              return (
                <div key={day} className={`flex items-center gap-4 py-2 ${isClosed ? 'opacity-50' : ''}`} data-testid={`hours-${day}`}>
                  <div className="w-24 flex-shrink-0">
                    <p className="text-sm font-medium text-[#1e3a5f] capitalize">{DAY_LABELS[day]}</p>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer flex-shrink-0">
                    <input type="checkbox" checked={isClosed || false} onChange={() => toggleDayClosed(day)}
                      className="w-4 h-4 accent-red-500" />
                    <span className="text-xs text-slate-500">Closed</span>
                  </label>
                  {!isClosed && (
                    <div className="flex items-center gap-2 flex-1">
                      <input type="time" value={hours.open || '09:00'} onChange={e => setHours(day, 'open', e.target.value)}
                        className="px-2 py-1.5 border-2 border-slate-200 rounded-sm text-sm focus:border-[#1e3a5f] focus:outline-none"
                        data-testid={`hours-${day}-open`} />
                      <span className="text-xs text-slate-400">to</span>
                      <input type="time" value={hours.close || '22:00'} onChange={e => setHours(day, 'close', e.target.value)}
                        className="px-2 py-1.5 border-2 border-slate-200 rounded-sm text-sm focus:border-[#1e3a5f] focus:outline-none"
                        data-testid={`hours-${day}-close`} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Section>

        {/* Social Media Automation */}
        <Section icon={Megaphone} title="Social Media Automation" desc="Automatically generate social posts when you receive new stock.">
          {/* Last auto-post outcome badge — always visible while it exists */}
          {socialSettings.last_auto_post && (
            <div
              className={`flex items-start gap-3 p-3 border-2 rounded-sm mb-5 ${
                socialSettings.last_auto_post.status === 'success'
                  ? 'bg-green-50 border-green-300'
                  : socialSettings.last_auto_post.status === 'skipped'
                  ? 'bg-slate-50 border-slate-300'
                  : 'bg-red-50 border-red-300'
              }`}
              data-testid="last-auto-post-badge"
            >
              {socialSettings.last_auto_post.status === 'success' ? (
                <CheckCircle className="w-5 h-5 text-green-700 flex-shrink-0 mt-0.5" />
              ) : socialSettings.last_auto_post.status === 'skipped' ? (
                <Timer className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className="w-5 h-5 text-red-700 flex-shrink-0 mt-0.5" />
              )}
              <div className="flex-1 text-sm">
                {socialSettings.last_auto_post.status === 'success' && (
                  <>
                    <p className="font-semibold text-green-900">
                      Last auto-post succeeded · {formatTimeAgo(socialSettings.last_auto_post.at)}
                    </p>
                    <p className="text-xs text-slate-600 mt-0.5">
                      Generated a {socialSettings.last_auto_post.mode === 'fully_automatic' ? 'published' : 'draft'} post for {socialSettings.last_auto_post.product_count || '?'} product(s).
                      {socialSettings.last_auto_post.post_id && (
                        <>
                          {' '}<a href="/shop/social" className="text-[#22c55e] underline font-medium">View on Social Posts →</a>
                        </>
                      )}
                    </p>
                  </>
                )}
                {socialSettings.last_auto_post.status === 'skipped' && (
                  <>
                    <p className="font-semibold text-slate-700">
                      Last intake didn't trigger an auto-post · {formatTimeAgo(socialSettings.last_auto_post.at)}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">{socialSettings.last_auto_post.reason}</p>
                  </>
                )}
                {socialSettings.last_auto_post.status === 'failed' && (
                  <>
                    <p className="font-semibold text-red-900">
                      Last auto-post failed · {formatTimeAgo(socialSettings.last_auto_post.at)}
                    </p>
                    <p className="text-xs text-red-700 mt-0.5">{socialSettings.last_auto_post.error}</p>
                  </>
                )}
                {socialSettings.last_auto_post.status === 'publish_failed' && (
                  <>
                    <p className="font-semibold text-red-900">
                      Auto-post was generated but couldn't be published · {formatTimeAgo(socialSettings.last_auto_post.at)}
                    </p>
                    <div className="text-xs text-red-700 mt-0.5">
                      {Object.entries(socialSettings.last_auto_post.platform_results || {}).map(([p, r]) => (
                        <span key={p} className="block">• <strong className="capitalize">{p}</strong>: {r.error || 'Unknown error'}</span>
                      ))}
                    </div>
                    <a href="/shop/social" className="text-xs text-[#22c55e] underline font-medium mt-1 inline-block">Retry from Social Posts →</a>
                  </>
                )}
              </div>
              <button onClick={dismissLastAutoPost}
                className="p-1 hover:bg-white rounded-sm text-slate-400 hover:text-slate-700"
                title="Dismiss"
                data-testid="dismiss-auto-post-badge">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {!socialConnections.facebook ? (
            <div className="p-4 border-2 border-dashed border-slate-300 rounded-sm bg-slate-50 text-center">
              <p className="text-sm text-slate-600 mb-2">No social accounts connected. Connect Facebook + Instagram first.</p>
              <a href="/shop/social" className="text-sm font-medium text-[#22c55e] hover:underline" data-testid="goto-social-connect">Go to Social Posts →</a>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Connection status banner */}
              <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-sm">
                <CheckCircle className="w-5 h-5 text-green-700 flex-shrink-0" />
                <div className="flex-1 text-sm">
                  <span className="font-medium text-[#1e3a5f]">{socialConnections.facebook.page_name}</span>
                  {socialConnections.facebook.ig_username && (
                    <span className="text-slate-600"> + @{socialConnections.facebook.ig_username}</span>
                  )}
                  <span className="text-slate-500"> are ready to receive automatic posts</span>
                </div>
              </div>

              {/* Enable toggle */}
              <Field label="Enable automation">
                <div className="flex items-center gap-3">
                  <Switch
                    checked={socialSettings.auto_post_enabled}
                    onCheckedChange={v => setSocialSettings(s => ({ ...s, auto_post_enabled: v }))}
                    data-testid="auto-post-enabled" />
                  <span className="text-sm text-slate-600">
                    {socialSettings.auto_post_enabled ? 'Automation is ON' : 'Automation is OFF'}
                  </span>
                </div>
              </Field>

              {/* Trigger mode */}
              {socialSettings.auto_post_enabled && (
                <>
                  <Field label="When new stock arrives">
                    <div className="space-y-2" data-testid="trigger-mode-group">
                      {[
                        { v: 'manual_only', t: 'Manual only', d: "Don't auto-generate. I'll create posts myself on the Social Posts page." },
                        { v: 'approval_queue', t: 'Generate drafts for me to approve', d: 'Auto-generate a post in draft status. I review and click Publish.' },
                        { v: 'fully_automatic', t: 'Generate AND publish automatically', d: 'No approval needed. Posts go live immediately after stock intake.' },
                      ].map(opt => (
                        <label key={opt.v}
                          className={`flex items-start gap-3 p-3 border-2 rounded-sm cursor-pointer transition-colors ${
                            socialSettings.auto_post_trigger === opt.v ? 'border-[#22c55e] bg-green-50' : 'border-slate-200 hover:bg-slate-50'
                          }`}
                          data-testid={`trigger-mode-${opt.v}`}>
                          <input type="radio" name="trigger-mode" value={opt.v}
                            checked={socialSettings.auto_post_trigger === opt.v}
                            onChange={() => setSocialSettings(s => ({ ...s, auto_post_trigger: opt.v }))}
                            className="mt-1 accent-[#22c55e]" />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-[#1e3a5f]">{opt.t}</p>
                            <p className="text-xs text-slate-500">{opt.d}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                    <p className="text-xs text-slate-400 mt-2">Auto-generation triggers when: 2+ products are added at once, OR any product goes from 0 → in stock.</p>
                  </Field>

                  {/* Platform selection (only relevant for fully_automatic) */}
                  {socialSettings.auto_post_trigger === 'fully_automatic' && (
                    <Field label="Auto-publish to">
                      <div className="space-y-2">
                        <label className={`flex items-center gap-3 p-3 border-2 rounded-sm cursor-pointer transition-colors ${
                          socialSettings.auto_post_platforms?.includes('facebook') ? 'border-blue-600 bg-blue-50' : 'border-slate-200 hover:bg-slate-50'
                        }`}>
                          <input type="checkbox"
                            checked={socialSettings.auto_post_platforms?.includes('facebook') || false}
                            onChange={() => toggleSocialPlatform('facebook')}
                            data-testid="auto-platform-fb" />
                          <Facebook className="w-5 h-5 text-blue-600" />
                          <span className="text-sm font-medium text-[#1e3a5f]">Facebook Page</span>
                        </label>
                        <label className={`flex items-center gap-3 p-3 border-2 rounded-sm cursor-pointer transition-colors ${
                          socialSettings.auto_post_platforms?.includes('instagram') ? 'border-pink-600 bg-pink-50' : 'border-slate-200 hover:bg-slate-50'
                        } ${!socialConnections.facebook?.ig_username ? 'opacity-50 cursor-not-allowed' : ''}`}>
                          <input type="checkbox"
                            disabled={!socialConnections.facebook?.ig_username}
                            checked={socialSettings.auto_post_platforms?.includes('instagram') || false}
                            onChange={() => toggleSocialPlatform('instagram')}
                            data-testid="auto-platform-ig" />
                          <Instagram className="w-5 h-5 text-pink-600" />
                          <span className="text-sm font-medium text-[#1e3a5f]">Instagram</span>
                          {!socialConnections.facebook?.ig_username && (
                            <span className="text-xs text-slate-400">(no IG Business linked)</span>
                          )}
                        </label>
                      </div>
                    </Field>
                  )}
                </>
              )}

              <div className="flex justify-end">
                <button onClick={handleSaveSocialAutomation} disabled={savingSocial}
                  className="vf-btn-primary flex items-center gap-2 disabled:opacity-50"
                  data-testid="save-automation-btn">
                  <Zap className="w-4 h-4" /> {savingSocial ? 'Saving...' : 'Save Automation'}
                </button>
              </div>
            </div>
          )}

          {/* Recent Auto-Post History — visible whenever we have entries (even after disconnect) */}
          <div className="pt-4 mt-4 border-t border-slate-100">
            <h3 className="text-sm font-bold text-[#1e3a5f] mb-3">Recent Auto-Posts</h3>
            <AutoPostHistory shopId={shopId} />
          </div>
        </Section>

        {/* Bottom Save */}
        <div className="flex justify-end pb-8">
          <button onClick={handleSave} disabled={saving}
            className="vf-btn-primary flex items-center gap-2 px-8 py-3 text-base disabled:opacity-50"
            data-testid="save-settings-bottom">
            <Save className="w-5 h-5" /> {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </ShopLayout>
  );
}
